import React, { useState, useEffect } from 'react';
import { getAllTestimonials, updateTestimonial, deleteTestimonial, addTestimonial } from '../../utils/firestore';
import { uploadImage } from '../../utils/imageUpload';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

const ManageTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formType, setFormType] = useState('create'); // 'create' or 'edit'
  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const testimonialsData = await getAllTestimonials();
        setTestimonials(testimonialsData);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        toast.error('Failed to fetch testimonials');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Set form values when editing a testimonial
  useEffect(() => {
    if (editingTestimonial) {
      setValue('name', editingTestimonial.name);
      setValue('comment', editingTestimonial.comment);
      setValue('rating', editingTestimonial.rating || 5);
      setValue('approved', editingTestimonial.approved || false);
      setValue('dateOfTestimonial', editingTestimonial.dateOfTestimonial ? new Date(editingTestimonial.dateOfTestimonial.toDate()).toISOString().split('T')[0] : '');
    } else {
      reset();
    }
  }, [editingTestimonial, reset, setValue]);

  const onSubmit = async (data) => {
    try {
      if (formType === 'create') {
        // Handle image upload
        let imageUrl = '';
        if (data.photo && data.photo[0]) {
          imageUrl = await uploadImage(data.photo[0], 'testimonial-images');
        }
        
        // Create testimonial
        const testimonialData = {
          name: data.name,
          comment: data.comment,
          rating: parseInt(data.rating),
          approved: data.approved === 'true', // Convert string to boolean
          photo: imageUrl,
          dateOfTestimonial: data.dateOfTestimonial ? new Date(data.dateOfTestimonial) : new Date(),
          createdAt: new Date()
        };
        
        await addTestimonial(testimonialData);
        toast.success('Yorum başarıyla oluşturuldu.');
      } else {
        // Handle image upload if new image provided
        let imageUrl = editingTestimonial.photo;
        if (data.photo && data.photo[0]) {
          imageUrl = await uploadImage(data.photo[0], 'testimonial-images');
        }
        
        // Update testimonial
        const testimonialData = {
          name: data.name,
          comment: data.comment,
          rating: parseInt(data.rating),
          approved: data.approved === 'true', // Convert string to boolean
          photo: imageUrl,
          dateOfTestimonial: data.dateOfTestimonial ? new Date(data.dateOfTestimonial) : editingTestimonial.dateOfTestimonial
        };
        
        await updateTestimonial(editingTestimonial.id, testimonialData);
        toast.success('Referans yorumu başarıyla güncellendi.');
      }
      
      // Refresh data and reset form
      const testimonialsData = await getAllTestimonials();
      setTestimonials(testimonialsData);
      setShowForm(false);
      setEditingTestimonial(null);
      reset();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      toast.error('Failed to save testimonial');
    }
  };

  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormType('edit');
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu referansı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) {
      try {
        await deleteTestimonial(id);
        const testimonialsData = await getAllTestimonials();
        setTestimonials(testimonialsData);
        toast.success('Referans yorumu başarıyla silindi.');
      } catch (error) {
        console.error('Error deleting testimonial:', error);
        toast.error('Failed to delete testimonial');
      }
    }
  };

  const toggleApproval = async (testimonial) => {
    try {
      await updateTestimonial(testimonial.id, { ...testimonial, approved: !testimonial.approved });
      const testimonialsData = await getAllTestimonials();
      setTestimonials(testimonialsData);
      toast.success(`Testimonial ${testimonial.approved ? 'un' : ''}approved successfully`);
    } catch (error) {
      console.error('Error updating testimonial approval:', error);
      toast.error(`Failed to ${testimonial.approved ? 'un' : ''}approve testimonial`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nihal-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Referans Yorumları Yönet</h1>
        <button
          onClick={() => {
            setFormType('create');
            setEditingTestimonial(null);
            setShowForm(true);
          }}
          className="btn btn-primary"
        >
          Yeni Referans Yorum Ekle
        </button>
      </div>

      {showForm ? (
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            {formType === 'create' ? 'Yeni Referans Yorum Ekle' : 'Yorum Düzenle'}
          </h2>
          
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  İsim Soyisim *
                </label>
                <input
                  id="name"
                  type="text"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  {...register('name', { required: 'İsim zorunlu' })}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="rating" className="block text-sm font-medium text-gray-700 mb-1">
                  Skor *
                </label>
                <select
                  id="rating"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary ${
                    errors.rating ? 'border-red-500' : 'border-gray-300'
                  }`}
                  {...register('rating', { required: 'Skor zorunlu' })}
                >
                  {[1, 2, 3, 4, 5].map((num) => (
                    <option key={num} value={num}>{num} Star{num !== 1 ? 's' : ''}</option>
                  ))}
                </select>
                {errors.rating && (
                  <p className="mt-1 text-sm text-red-600">{errors.rating.message}</p>
                )}
              </div>
            </div>
            
            <div className="mb-6">
              <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-1">
                Yorum *
              </label>
              <textarea
                id="comment"
                rows="4"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary ${
                  errors.comment ? 'border-red-500' : 'border-gray-300'
                }`}
                {...register('comment', { required: 'Yorum zorunlu' })}
              ></textarea>
              {errors.comment && (
                <p className="mt-1 text-sm text-red-600">{errors.comment.message}</p>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="dateOfTestimonial" className="block text-sm font-medium text-gray-700 mb-1">
                  Yorum Tarihi
                </label>
                <input
                  id="dateOfTestimonial"
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                  {...register('dateOfTestimonial')}
                />
              </div>
              
              <div>
                <label htmlFor="approved" className="block text-sm font-medium text-gray-700 mb-1">
                  Statüsü
                </label>
                <select
                  id="approved"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                  {...register('approved')}
                >
                  <option value="true">Kabul Edildi</option>
                  <option value="false">Beklemede</option>
                </select>
              </div>
            </div>
            
            <div className="mb-6">
              <label htmlFor="photo" className="block text-sm font-medium text-gray-700 mb-1">
                Görsel
              </label>
              <input
                id="photo"
                type="file"
                accept="image/*"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                {...register('photo')}
              />
              {editingTestimonial?.photo && !register('photo').value && (
                <div className="mt-2">
                  <p className="text-sm text-gray-600">Current photo:</p>
                  <img 
                    src={editingTestimonial.photo} 
                    alt="Current" 
                    className="w-32 h-32 object-cover mt-2 rounded"
                  />
                </div>
              )}
            </div>
            
            <div className="flex space-x-4">
              <button
                type="submit"
                className="btn btn-primary"
              >
                {formType === 'create' ? 'Referans Yorum Ekle' : 'Referans Yorum Güncelle'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingTestimonial(null);
                }}
                className="btn btn-secondary"
              >
                İptal
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                İsim Soyisim
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Yorum
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Skor
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statü
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Eylemler
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {testimonials.map((testimonial) => (
              <tr key={testimonial.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {testimonial.photo ? (
                      <img 
                        src={testimonial.photo} 
                        alt={testimonial.name} 
                        className="w-10 h-10 rounded-full object-cover mr-3"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-nihal-primary flex items-center justify-center text-white mr-3">
                        <span className="font-bold">{testimonial.name.charAt(0)}</span>
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-medium text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-500">
                        {testimonial.dateOfTestimonial ? new Date(testimonial.dateOfTestimonial.toDate()).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 line-clamp-2">{testimonial.comment}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-5 h-5 ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span 
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      testimonial.approved 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {testimonial.approved ? 'Kabul Edildi' : 'Beklemede'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => toggleApproval(testimonial)}
                    className={`mr-4 ${
                      testimonial.approved 
                        ? 'text-yellow-600 hover:text-yellow-900' 
                        : 'text-green-600 hover:text-green-900'
                    }`}
                  >
                    {testimonial.approved ? 'Reddet' : 'Kabul Et'}
                  </button>
                  <button
                    onClick={() => handleEdit(testimonial)}
                    className="text-blue-600 hover:text-indigo-900 mr-4"
                  >
                    Düzenle
                  </button>
                  <button
                    onClick={() => handleDelete(testimonial.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Sil
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        
        {testimonials.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Referans yorum bulunmamaktadır.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageTestimonials;